import { pick } from 'lodash-es';
import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import { getNullableMoment } from '@intelligence/v3/utils/date';
import { formatAppMagicGenres, generateGenreSelectValueIntelligence } from '@intelligence/v3/utils/genre';
import { buildSensorTowerTagsParams, buildMetricFiltersFetchParams } from '@intelligence/v3/utils/topcharts';
import { generateMetricKey } from '@intelligence/v3/utils/metric';
import { formatGenerePageFunc } from '@intelligence/components/Filter/util';
import { Relation } from '@intelligence/components/GenreSelect/enums';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import { METRIC_THRESHOLD_CONFIG, DEFAULT_THRESHOLD, THRESHOLD_OPERATOR_MAP } from './metricThresholdConfig';
import { GENRE_SOURCE_TO_PRODUCT_MODEL_MAP } from '../constants';

import type { IOptionWithChildren, IGenreSource } from '@intelligence/components/GenreSelect/types';
import type { TDataSourceType, TMetricType, TDimension } from '@intelligence/v3/pages/CasualGenreProfile/types';
import type { IMetricFilter, IFilterParams, IThresholdResult } from './metricThresholdConfig';

/**
 * ============================================================================
 * Rising Games 指标阈值计算模块
 * ============================================================================
 * 用于根据时间跨度动态计算 Rising Games 筛选的指标阈值
 */
/**
 * 计算日期差值（指定单位）
 *
 * @param startDate - 开始日期字符串
 * @param endDate - 结束日期字符串
 * @param unit - 差值单位 ('days' | 'months')
 * @returns 日期差值，如果日期无效则返回 null
 *
 * @example
 * calculateDateDiff('2026-01-01', '2026-01-03', 'days') // => 2
 * calculateDateDiff('2026-01-01', '2026-03-01', 'months') // => 2
 */
const calculateDateDiff = (startDate: string, endDate: string, unit: 'days' | 'months'): number | null => {
  const startDateM = getNullableMoment(startDate);
  const endDateM = getNullableMoment(endDate);

  if (!startDateM || !endDateM) {
    return null;
  }

  // 确保结束日期不早于开始日期
  if (endDateM.isBefore(startDateM)) {
    return null;
  }

  return endDateM.diff(startDateM, unit);
};

/**
 * 根据配置计算指标阈值
 *
 * @param metricKey - 指标键名 (格式: 'dataSource__metric')
 * @param granularity - 时间粒度
 * @param startDate - 开始日期
 * @param endDate - 结束日期
 * @returns 阈值计算结果
 *
 * @example
 * calculateMetricThreshold('sensortower__download', 'daily', '2026-01-01', '2026-01-03')
 * // => { min: 150000, max: null } (3天 * 50,000 = 150,000)
 */
const calculateMetricThreshold = (
  metricKey: string,
  granularity: IGranularity,
  startDate: string,
  endDate: string,
): IThresholdResult => {
  const configKey = `${granularity}__${metricKey}` as keyof typeof METRIC_THRESHOLD_CONFIG;
  const config = METRIC_THRESHOLD_CONFIG[configKey];

  // 如果配置不存在，返回默认值
  if (!config) {
    return DEFAULT_THRESHOLD;
  }

  if (['sensortower__dau', 'sensortower__mau'].includes(metricKey)) {
    return { min: config.baseValue, max: null };
  }

  const dateDiff = calculateDateDiff(startDate, endDate, config.unit);

  // 如果日期计算失败，返回默认值
  if (dateDiff === null) {
    return DEFAULT_THRESHOLD;
  }

  // 计算阈值
  return {
    min: dateDiff * config.baseValue,
    max: null,
  };
};

/**
 * 获取指标阈值过滤条件
 *
 * 用于 Rising Games 筛选，根据选择的数据源、指标和日期范围动态生成过滤条件
 *
 * @param dataSource - 数据源类型
 * @param metric - 指标类型
 * @param filterParams - 筛选器参数
 * @returns 指标过滤条件数组
 *
 * @example
 * getMetricFiltersOfMetricValueLimit(
 *   'sensortower',
 *   'download',
 *   { granularity: 'daily', start_date: '2026-01-01', end_date: '2026-01-03' }
 * )
 * // => [{ metric: 'sensortower__download', metricValue: { opt: '>', value: 150000 } }]
 */
export const getMetricFiltersOfMetricValueLimit = (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: IFilterParams,
): IMetricFilter[] => {
  const granularity = (filterParams?.granularity ?? 'monthly') as IGranularity;
  const startDate = filterParams?.start_date ?? '';
  const endDate = filterParams?.end_date ?? '';

  const dataSourceMetric = generateMetricKey({ dataSource, metric });
  const threshold = calculateMetricThreshold(dataSourceMetric, granularity, startDate, endDate);

  const metricFilters: IMetricFilter[] = [];

  // 遍历阈值结果，生成过滤条件
  (Object.entries(threshold) as Array<[keyof IThresholdResult, number | null]>).forEach(([key, value]) => {
    if (value != null) {
      const opt = THRESHOLD_OPERATOR_MAP[key];
      metricFilters.push({
        metric: dataSourceMetric,
        metricValue: { opt, value },
      });
    }
  });

  return metricFilters;
};

export const getMetricValueIntelligence = (params: {
  dataSource: TDataSourceType;
  metric: TMetricType;
  ratio?: boolean;
  data?: ICasualGenreProfileMetricData | null;
}): number | null => {
  const dataSource = params?.dataSource || '';
  const metric = params?.metric || '';
  const ratio = params?.ratio || false;
  const data = params?.data || null;

  if (isEmpty(data)) {
    return null;
  }

  const metricKey = generateMetricKey({ dataSource, metric, ratio });
  return (data?.[metricKey as keyof ICasualGenreProfileMetricData] as number) ?? null;
};

export const formatFilterFetchParams = (filterParams: any) => {
  const markets = filterParams?.regions;
  const platforms = filterParams?.platforms;
  const sensortowerGenres = filterParams?.sensortower_genre;
  const appmagicGenres = filterParams?.appmagic_genre;
  const appMagicTags = filterParams?.app_magic_tags;
  const companies = filterParams?.company;
  const granularity = filterParams?.granularity || 'monthly';
  const startReleaseDate = filterParams?.start_release_time || '';
  const endReleaseDate = filterParams?.end_release_time || '';

  const metricFilter = buildMetricFiltersFetchParams(filterParams?.metric_filter);

  return {
    markets: isNotEmpty(markets) ? markets : ['global'],
    platforms: isNotEmpty(platforms) ? platforms : ['appstore', 'googleplay'],
    granularity: granularity || 'monthly',
    start_date: filterParams?.start_date || '',
    end_date: filterParams?.end_date || '',
    filter: {
      sensortower_genre: sensortowerGenres,
      tags: buildSensorTowerTagsParams(filterParams?.sensortower_tag_filter),
      app_magic: {
        relation: 'or' as Relation,
        tags: formatAppMagicGenres(appmagicGenres),
      },
      app_magic_tags: isNotEmpty(appMagicTags) ? appMagicTags : {},
      companies: isNotEmpty(companies) ? (companies as any as string[]) : [],
      metric_filter: metricFilter,
      start_release_date: startReleaseDate || '',
      end_release_date: endReleaseDate || '',
    },
  };
};

/**
 * ============================================================================
 * 品类关键字生成模块
 * ============================================================================
 * 用于根据搜索关键字从品类选项中生成品类关键字数组
 */

/**
 * 根据品类维度获取对应的层级
 * @param dimension 品类维度
 * @returns 层级数字 (1-4)
 */
const getLevelByDimension = (dimension: TDimension): number => {
  // SensorTower
  if (dimension === 'mainGenre') return 1;
  if (dimension === 'subGenre') return 2;
  // AppMagic
  if (dimension === 'superGenre') return 1;
  if (dimension === 'majorCategory') return 2;
  if (dimension === 'midCategory') return 3;
  if (dimension === 'subCategory') return 4;
  return 1; // 默认第一层
};

/**
 * 递归收集符合条件的品类英文名
 * @param options 品类选项数组
 * @param currentLevel 当前层级
 * @param targetLevel 目标层级
 * @param trimmedKeyword 搜索关键字（已去除首尾空格）
 * @param keywords 结果数组
 */
const collectMatchingGenres = (
  options: IOptionWithChildren[],
  currentLevel: number,
  targetLevel: number,
  trimmedKeyword: string,
  keywords: string[],
): void => {
  for (const option of options) {
    // 同时匹配中英文名称
    const textMatches = option?.text && option?.text?.toLowerCase()?.includes(trimmedKeyword?.toLowerCase());
    const textCNMatches = option?.textCN && option?.textCN?.toLowerCase()?.includes(trimmedKeyword?.toLowerCase());

    // 检查是否匹配（中文名称或英文名称任一匹配即可），且只收集目标层级的品类
    if ((textMatches || textCNMatches) && currentLevel === targetLevel) {
      keywords.push(option.value);
    }

    // 递归处理子品类（只匹配目标层级）
    if (option?.children && option.children.length > 0) {
      collectMatchingGenres(option.children, currentLevel + 1, targetLevel, trimmedKeyword, keywords);
    }
  }
};

/**
 * 根据搜索关键字生成品类关键字数组
 *
 * @param params 参数对象
 * @param params.genreSource 品类数据源
 * @param params.genreDimension 品类维度
 * @param params.keyword 搜索关键字
 * @param params.genreOptionsMap 品类选项数据 map
 * @returns 品类英文名数组（已去重）
 *
 * @example
 * getGenreKeywordsFromKeyword({
 *   genreSource: 'sensortower',
 *   genreDimension: 'mainGenre',
 *   keyword: 'Puzzle',
 *   genreOptionsMap: { sensortower: [...] },
 * })
 * // => ['puzzle', 'puzzle_2', ...]
 */
export const getGenreKeywordsFromKeyword = (params: {
  genreSource: IGenreSource;
  genreOptionsMap: IRecord<IOptionWithChildren[]>;
  genreDimension: TDimension;
  keyword: string;
}): string[] => {
  const { genreSource, genreOptionsMap, genreDimension, keyword } = params || {};

  // 空关键字返回空数组
  if (!keyword?.trim()) {
    return [];
  }

  // 获取当前数据源的品类选项（兼容 appmagic 和 app_magic 两种键名）
  const options = genreOptionsMap?.[genreSource] || [];

  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  const keywords: string[] = [];
  const trimmedKeyword = keyword.trim();
  const targetLevel = getLevelByDimension(genreDimension);

  // 递归收集符合条件的品类英文名
  collectMatchingGenres(options, 1, targetLevel, trimmedKeyword, keywords);

  // 去重
  return Array.from(new Set(keywords));
};

/**
 * 根据品类数据源获取对应的 Product Model 筛选器值
 * @param genreSource 品类数据源 ('sensortower' | 'appmagic')
 * @returns Product Model 筛选器应该勾选的值数组
 */
export const getProductModelValueByGenreSource = (genreSource: string): string[] => {
  return GENRE_SOURCE_TO_PRODUCT_MODEL_MAP[genreSource] ?? [];
};

/**
 * ============================================================================
 * 图表点击筛选模块
 * ============================================================================
 * 用于处理品类图表点击事件，构建筛选参数
 */

/**
 * 品类维度到层级的映射
 */
const GENRE_DIMENSION_LEVEL_MAP: Record<TDimension, number> = {
  mainGenre: 1,
  subGenre: 2,
  superGenre: 1,
  majorCategory: 2,
  midCategory: 3,
  subCategory: 4,
};

/**
 * 图表点击时构建品类筛选参数（返回值）
 */
export interface IBuildGenreFilterResult {
  /** 新的筛选器值 */
  filterValueNew: IRecord;
  /** 新的筛选参数（含API格式） */
  filterValueNewWithApiParams: IRecord;
  /** 更新参数（用于各section update） */
  updateParams: {
    genreSource: IGenreSource;
    dataSource: TDataSourceType;
    metric: TMetricType;
    filterParams: IRecord;
  };
  /** 品类维度 */
  genreDimension: TDimension;
  /** 新的品类数据源 */
  genreSourceNew: IGenreSource;
}

/**
 * 图表点击时构建品类筛选参数
 * @param params 参数对象
 * @returns 包含筛选器值和更新参数的对象
 */
export const buildGenreFilterParamsByChartClick = (params: {
  /** 品类选项数据map */
  genreOptionsMap: IRecord<IOptionWithChildren[]>;
  /** 品类数据源 */
  genreSource: IGenreSource;
  /** 品类维度 */
  genreDimension: TDimension;
  /** 品类名称 */
  genreName: string;
  /** 数据源 */
  dataSource: TDataSourceType;
  /** 指标 */
  metric: TMetricType;
  /** 当前筛选器值 */
  filterValue: IRecord;
  /** 当前筛选参数 */
  filterParams: IRecord;
}): IBuildGenreFilterResult => {
  const { genreOptionsMap, genreSource, genreDimension, genreName, dataSource, metric, filterValue, filterParams } =
    params;

  const genreDimensionLevel = GENRE_DIMENSION_LEVEL_MAP[genreDimension] || 1;

  const genretorValue = filterValue?.[filterKeyMap.GENRETOR_WITH_SEGMENT];
  const genretorValueNew = generateGenreSelectValueIntelligence(
    genreOptionsMap,
    genretorValue,
    genreSource,
    genreDimensionLevel,
    genreName,
  );
  const genreSourceNew = genretorValueNew?.source;

  // 构建新的筛选器值
  const filterValueNew: any = {
    ...filterValue,
    [filterKeyMap.GENRETOR_WITH_SEGMENT]: {
      source: genreSourceNew,
      mainGenres: genretorValueNew?.mainGenres || [],
      subGenres: genretorValueNew?.subGenres || [],
      // App Magic 品类使用 level3Genres 和 level4Genres
      level3Genres: genretorValueNew?.level3Genres || [],
      level4Genres: genretorValueNew?.level4Genres || [],
    },
  };

  // 转换品类参数为 API 格式
  const genreFilterValue = filterValueNew?.[filterKeyMap.GENRETOR_WITH_SEGMENT];
  const convertedGenreParams = formatGenerePageFunc(
    {
      ...genreFilterValue,
      options: genreOptionsMap?.[genreFilterValue?.source] || [],
    },
    '',
    { isStdGenreValuesHandler: true },
  );

  // 将转换后的品类参数合并
  const filterValueNewWithApiParams = {
    ...filterValueNew,
    ...convertedGenreParams,
  };

  const filterParamsNew = {
    ...filterParams,
    ...pick(convertedGenreParams, [
      'genre_source',
      'iegg_genre',
      'sensortower_genre',
      'appmagic_genre',
      'appannie_genre',
    ]),
  };

  const updateParams = {
    genreSource: genreSourceNew,
    dataSource,
    metric,
    filterParams: filterParamsNew,
  };

  return {
    filterValueNew,
    filterValueNewWithApiParams,
    updateParams,
    genreDimension,
    genreSourceNew,
  };
};
